import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';

import { getStopById } from 'Global/JourneyElements/Utils/getStopById';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { isBeforeNow } from 'Util/_HumanWritten/GeneralHelpers/DateTimeHelpers';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import alertToast from 'Util/ToastifyUtils';
import { BookingListSummaryDto } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import { BookingStatusTag } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { FreightIcon } from '../../Icon/IconHelpers';

export interface BookingCardProps {
	booking: BookingListSummaryDto;
	editUrl: string;
}

function getIconConfig(bookingType: string) {
	switch (bookingType) {
		case 'EVENT_BOOKING':
			return { className: 'booking-header__icon--event', icon: 'event' };
		case 'FREIGHT':
			return { className: 'booking-header__icon--freight', icon: 'freight' };
		case 'VEHICLE':
			return { className: 'booking-header__icon--ferry', icon: 'vehicle' };
		default:
			return { className: 'booking-header__icon--ferry', icon: 'person' };
	}
}

function getBookingTypeLabel(bookingType: string) {
	switch (bookingType) {
		case 'EVENT_BOOKING':
			return 'Event';
		case 'FREIGHT':
			return 'Ferry · Freight';
		case 'VEHICLE':
			return 'Ferry · Vehicle';
		case 'PASSENGER_ONLY':
			return 'Ferry · Walk-on';
		default:
			return 'Ferry';
	}
}

export function BookingCard({ booking, editUrl }: BookingCardProps) {
	if (isNullOrUndefined(booking)) {
		alertToast('Booking is missing details', 'error', undefined, { autoClose: 3000 });
		throw new Error('Booking is missing details');
	}

	const {
		passengerDetailsString,
		cargoDriverDetailsString,
		cargoRegoAndTrailerDetailsString,
		freightDetailsString,
		eventName,
		totalCost,
		tripDateTime,
		route,
		iconName,
		status,
		bookingType,
	} = booking;

	let startStopName = route?.departure?.name;
	let endStopName = route?.destination?.name;

	if (booking?.startStopId) {
		const stop = getStopById(booking.startStopId);
		if (stop) startStopName = stop.location.name;
	}
	if (booking?.endStopId) {
		const stop = getStopById(booking.endStopId);
		if (stop) endStopName = stop.location.name;
	}

	const routeName = bookingType === 'EVENT_BOOKING' ? eventName : `${startStopName} to ${endStopName}`;

	const iconConfig = getIconConfig(bookingType);
	const isPast = isBeforeNow(tripDateTime);
	const isCancelled = status === 'CANCELLED';
	const isUnpaid = status === 'UNPAID';

	const renderIcon = () => {
		if (bookingType === 'EVENT_BOOKING' && iconName) {
			return <Icon name={iconName} useMaterialIcons />;
		}
		if (bookingType === 'FREIGHT') {
			return <FreightIcon />;
		}
		if (bookingType === 'VEHICLE') {
			return <Icon name="directions_car" useMaterialIcons />;
		}
		return <Icon name="person" useMaterialIcons />;
	};

	const linkText = isPast || isCancelled || isUnpaid ? 'View booking' : 'View/Edit booking';

	return (
		<Link to={editUrl} className="booking-card-container hide-underline">
			<div className="booking">
				<div className="booking-header">
					<div className="booking-header__left">
						<div className={`booking-header__icon ${iconConfig.className}`}>{renderIcon()}</div>
						<div className="booking-header__info">
							<div className="booking-header__type-row">
								<span className="booking-header__type">{getBookingTypeLabel(bookingType)}</span>
								<span className="booking-header__id booking-header__id--mobile">
									#{booking.humanReadableId}
								</span>
							</div>
							<div className="booking-header__title-row">
								<h4 className="booking-header__title">{routeName}</h4>
								<div className="booking-header__status--mobile">
									{BookingStatusTag({ status: booking.status })}
								</div>
							</div>
							<div className="booking-header__meta">
								<span>
									<Icon name="calendar_today" useMaterialIcons />
									{moment(tripDateTime).format('DD MMM YYYY')}
								</span>
								<span>
									<Icon name="schedule" useMaterialIcons />
									{formatStandardTime(tripDateTime)}
								</span>
							</div>
						</div>
					</div>
					<div className="booking-header__right">
						<span className="booking-header__id booking-header__id--desktop">
							#{booking.humanReadableId}
						</span>
						<div className="tags__container">{BookingStatusTag({ status: booking.status })}</div>
						<span className="booking-header__price">{formatPriceDisplay(totalCost, false)}</span>
					</div>
				</div>
				<div className="ticket-body">
					{bookingType !== 'FREIGHT' && (
						<div className="ticket-body__item">
							<p className="ticket-body__label">
								{bookingType === 'EVENT_BOOKING' ? 'Tickets' : 'Passengers'}
							</p>
							<p className="ticket-body__value">
								{passengerDetailsString || (bookingType === 'VEHICLE' ? 'None' : '-')}
							</p>
						</div>
					)}
					{stringNotEmpty(cargoRegoAndTrailerDetailsString) && (
						<div className="ticket-body__item">
							<p className="ticket-body__label">Vehicle</p>
							<p className="ticket-body__value">{cargoRegoAndTrailerDetailsString}</p>
						</div>
					)}
					{stringNotEmpty(cargoDriverDetailsString) && (
						<div className="ticket-body__item">
							<p className="ticket-body__label">Driver</p>
							<p className="ticket-body__value">{cargoDriverDetailsString.replace(/\s*\(.*\)$/, '')}</p>
						</div>
					)}
					{stringNotEmpty(freightDetailsString) && (
						<div className="ticket-body__item">
							<p className="ticket-body__label">Freight</p>
							<p className="ticket-body__value">{freightDetailsString}</p>
						</div>
					)}
				</div>
			</div>
			<span className="view-booking-detail-section__edit-button">{linkText}</span>
		</Link>
	);
}
